// ============================================================================
// REPOSITORY: ClientDriveViewRepository — Fase 6.3
// Abstrai TODAS as chamadas Firestore do ClientDriveView.jsx
// ============================================================================

import {
  addDoc, arrayRemove, arrayUnion, collection, deleteDoc, deleteField,
  doc, getDocs, limit, onSnapshot, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../../shared/api/firebase/config';

// ── 1. Document Reads (telemetria de visualização) ──────────────────────────
export async function logDocumentRead(appId, clientId, fileId, fileName) {
  if (!appId || !clientId) return;
  try {
    await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'document_reads'), {
      clientId: String(clientId || '').trim(),
      fileId: String(fileId || '').trim(),
      fileName: String(fileName || '').trim(),
      readAt: serverTimestamp(),
      deviceType: window.innerWidth < 768 ? 'mobile' : 'desktop',
    });
  } catch {
    // silently ignore telemetry errors
  }
}

// ── ITEM G (26/08/2026) — resolve a QUAL PEÇA um folderId pertence ─────────
// Cadernos com múltiplas peças (criados após 21/08/2026) dão a cada peça sua
// própria pasta — o campo fica dentro de cada entrada de `pieces[]`
// (processPieceFolderId/attachmentsFolderId), não mais só no topo do
// documento. Cadernos de 1 peça só (ex.: [CLIENTE_A]) não têm esse campo por
// peça — o topo do documento já resolve sem ambiguidade nesse caso. Retorna
// null quando não dá pra determinar (nunca inventa/assume) — quem chama deve
// tratar null como "escreve só no legado, como sempre fez", nunca como erro.
export function resolvePieceKeyFromFolder(workspaceData, folderId) {
  const pieces = Array.isArray(workspaceData?.pieces) ? workspaceData.pieces : [];
  for (const piece of pieces) {
    const candidates = [piece?.processPieceFolderId, piece?.attachmentsFolderId].map((v) => String(v || '').trim());
    if (candidates.includes(String(folderId || '').trim())) {
      return String(piece?.id || '').trim() || null;
    }
  }
  return null;
}

// ── 2. Workspace por Folder (onSnapshot lazy + busca por campo) ────────────
export function subscribeWorkspaceByFolder(appId, folderId, onData, onError) {
  if (!appId || !folderId) return () => {};
  const col = collection(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces');
  const tryFields = ['processPieceFolderId', 'attachmentsFolderId', 'driveFolderId'];
  let unsub = null;

  (async () => {
    try {
      for (const field of tryFields) {
        const s = await getDocs(query(col, where(field, '==', folderId), limit(1)));
        if (!s.empty) {
          unsub = onSnapshot(
            s.docs[0].ref,
            (snap) => { if (snap.exists()) onData(snap); },
            onError || (() => {})
          );
          break;
        }
      }
    } catch {
      if (onError) onError();
    }
  })();

  return () => {
    if (unsub) unsub();
  };
}

// ── 3. Triage Intent (SAGA Passo 1) ────────────────────────────────────────
export async function createTriageIntent(appId, intentPayload) {
  let sagaDocRef = null;
  try {
    sagaDocRef = await addDoc(collection(db, 'rda_triage'), intentPayload);
  } catch {
    if (appId) {
      sagaDocRef = await addDoc(
        collection(db, 'artifacts', appId, 'public', 'data', 'rda_triage'),
        intentPayload
      ).catch(() => null);
    }
  }
  return sagaDocRef;
}

// ── 4. Triage Commit (SAGA Passo 3) ────────────────────────────────────────
export async function commitTriageIntent(sagaDocRef, driveFileId, fileUrl) {
  if (!sagaDocRef) return false;
  try {
    await updateDoc(sagaDocRef, {
      driveFileId,
      fileUrl,
      downloadUrl: fileUrl,
      storageEngine: { provider: 'gdrive', fileId: driveFileId, webViewLink: fileUrl },
      status: 'Aguardando Triagem',
    });
    return true;
  } catch {
    return false;
  }
}

// ── 5. Triage Fallback Commit ──────────────────────────────────────────────
export async function fallbackTriageCommit(appId, payload) {
  if (!appId) return null;
  try {
    return await addDoc(
      collection(db, 'artifacts', appId, 'public', 'data', 'rda_triage'),
      payload
    );
  } catch {
    return null;
  }
}

// ── 6. Client Note (upload submitted) ──────────────────────────────────────
export async function addClientNote(appId, clientId, notePayload) {
  if (!appId || !clientId) return null;
  try {
    return await addDoc(
      collection(db, 'artifacts', appId, 'public', 'data', 'clients', clientId, 'notes'),
      notePayload
    );
  } catch {
    return null;
  }
}

// ── 7. Workspace Assignment (após upload) ──────────────────────────────────
// ITEM G (26/08/2026): pieceKey opcional — quando resolvido (ver
// resolvePieceKeyFromFolder), grava TAMBÉM em organizerStates.{pieceKey},
// além do legado organizerState (dual-write consciente, Art. 201.5 da
// Constituição Anti-Retrocesso: só quando há conteúdo de verdade, nunca em
// reset). Sem pieceKey, comportamento idêntico ao de antes — só legado.
// Usa updateDoc() com string de caminho (split nativo de ponto), nunca
// setDoc(merge:true) com chave computada — é o padrão seguro já confirmado
// nesta investigação (ver commit do item E).
export async function updateWorkspaceAssignment(appId, wsId, driveFileId, safeName, fileUrl, assignSlotId, extraSlotMeta, pieceKey = null) {
  if (!appId || !wsId || !driveFileId || !assignSlotId) return false;
  try {
    const wsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', wsId);
    const updatePayload = {
      [`organizerState.assignments.${assignSlotId}.id`]: driveFileId,
      [`organizerState.assignments.${assignSlotId}.name`]: safeName,
      [`organizerState.assignments.${assignSlotId}.url`]: fileUrl,
      [`organizerState.assignments.${assignSlotId}.multiFiles`]: arrayUnion({ id: driveFileId, name: safeName, url: fileUrl }),
      updatedAt: serverTimestamp(),
    };
    if (extraSlotMeta?.name) {
      updatePayload['organizerState.slotOrder'] = arrayUnion(assignSlotId);
      updatePayload[`organizerState.slotNames.${assignSlotId}`] = extraSlotMeta.name;
      updatePayload[`organizerState.clientRequested.${assignSlotId}`] = true;
    }
    const pk = String(pieceKey || '').trim();
    if (pk) {
      updatePayload[`organizerStates.${pk}.assignments.${assignSlotId}.id`] = driveFileId;
      updatePayload[`organizerStates.${pk}.assignments.${assignSlotId}.name`] = safeName;
      updatePayload[`organizerStates.${pk}.assignments.${assignSlotId}.url`] = fileUrl;
      updatePayload[`organizerStates.${pk}.assignments.${assignSlotId}.multiFiles`] = arrayUnion({ id: driveFileId, name: safeName, url: fileUrl });
      if (extraSlotMeta?.name) {
        updatePayload[`organizerStates.${pk}.slotOrder`] = arrayUnion(assignSlotId);
        updatePayload[`organizerStates.${pk}.slotNames.${assignSlotId}`] = extraSlotMeta.name;
        updatePayload[`organizerStates.${pk}.clientRequested.${assignSlotId}`] = true;
      }
    }
    await updateDoc(wsDocRef, updatePayload);
    return true;
  } catch {
    return false;
  }
}

// ── 8. Rollback Intent ─────────────────────────────────────────────────────
export async function deleteSagaDoc(sagaDocRef) {
  if (!sagaDocRef) return;
  try { await deleteDoc(sagaDocRef); } catch { /* best-effort cleanup */ }
}

// ── 9. Rename Slot ─────────────────────────────────────────────────────────
// ITEM G (26/08/2026): pieceKey opcional em todas as funções abaixo, mesmo
// padrão de updateWorkspaceAssignment — ver comentário lá.
export async function renameSlot(appId, wsId, slotId, newName, pieceKey = null) {
  if (!appId || !wsId || !slotId || !newName) return false;
  try {
    const wsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', wsId);
    const pk = String(pieceKey || '').trim();
    await updateDoc(wsDocRef, {
      [`organizerState.slotNames.${slotId}`]: newName,
      ...(pk ? { [`organizerStates.${pk}.slotNames.${slotId}`]: newName } : {}),
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch {
    return false;
  }
}

// ── 10. Upload Lock / Unlock ───────────────────────────────────────────────
// _uploadLocks é de coordenação de UI (evitar 2 uploads simultâneos no mesmo
// slot), não é dado de Anexo em si — mantido só no topo, não precisa de
// pieceKey (o risco de contaminação cruzada é sobre CONTEÚDO, não sobre lock
// transitório).
export async function setUploadLock(appId, wsId, slotId) {
  if (!appId || !wsId || !slotId) return false;
  try {
    const wsLockRef = doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', wsId);
    await updateDoc(wsLockRef, {
      [`_uploadLocks.${slotId}`]: Date.now(),
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch {
    return false;
  }
}

export async function clearUploadLock(appId, wsId, slotId) {
  if (!appId || !wsId || !slotId) return false;
  try {
    const wsLockRef = doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', wsId);
    await updateDoc(wsLockRef, {
      [`_uploadLocks.${slotId}`]: 0,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch {
    return false;
  }
}

// ── 11. Remove Extra Slot ──────────────────────────────────────────────────
export async function removeExtraSlot(appId, wsId, slotId, pieceKey = null) {
  if (!appId || !wsId || !slotId) return false;
  try {
    const wsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', wsId);
    const pk = String(pieceKey || '').trim();
    await updateDoc(wsDocRef, {
      'organizerState.slotOrder': arrayRemove(slotId),
      [`organizerState.slotNames.${slotId}`]: deleteField(),
      [`organizerState.clientRequested.${slotId}`]: deleteField(),
      ...(pk
        ? {
            [`organizerStates.${pk}.slotOrder`]: arrayRemove(slotId),
            [`organizerStates.${pk}.slotNames.${slotId}`]: deleteField(),
            [`organizerStates.${pk}.clientRequested.${slotId}`]: deleteField(),
          }
        : {}),
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch {
    return false;
  }
}

// ── 12. Add Extra Slot ───────────────────────────────────────────────────
export async function addExtraSlot(appId, wsId, slotId, pieceKey = null) {
  if (!appId || !wsId || !slotId) return false;
  try {
    const wsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', wsId);
    const pk = String(pieceKey || '').trim();
    await updateDoc(wsDocRef, {
      'organizerState.slotOrder': arrayUnion(slotId),
      [`organizerState.clientRequested.${slotId}`]: true,
      ...(pk
        ? {
            [`organizerStates.${pk}.slotOrder`]: arrayUnion(slotId),
            [`organizerStates.${pk}.clientRequested.${slotId}`]: true,
          }
        : {}),
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch {
    return false;
  }
}

// ── 13. Subscribe Workspaces por Client (main component) ─────────────────────
export function subscribeWorkspacesByClient(appId, clientId, onData, onError) {
  if (!appId || !clientId) return () => {};
  const col = collection(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces');
  const q = query(col, where('clientId', '==', String(clientId || '').trim()));
  return onSnapshot(q, onData, onError || (() => {}));
}

// ── Re-export utilitários Firestore usados pelo componente ─────────────────
export { serverTimestamp } from 'firebase/firestore';
